import { useRef, useState } from 'react';
import './Testimonials.css';
import Icon from './Icon';
import useReveal from './useReveal';

// §5.10 — real testimonial / session videos. Each card shows a still frame pulled
// from the video itself as a thumbnail, plus a play button; clicking plays it inline
// with native controls.
const REELS = [
  {
    src: '/assets/video/nishigandha-devare.mp4',
    name: 'Nishigandha Mangesh Devare',
    company: 'Podar International School',
    title: 'IBDP Biology Teacher',
  },
  {
    src: '/assets/video/abhijat-sanghvi.mp4',
    name: 'Mr. Abhijat Sanghvi',
    company: 'Sang Fasteners Pvt. Ltd',
    title: 'Director',
  },
  {
    src: '/assets/video/future-ready-teens.mp4',
    name: 'Tejashree',
    company: 'Assistant Business Development Manager',
    title: 'Navneet Education Limited (Export)',
  },
];

// Seconds into each clip to grab the thumbnail frame (skips a possible black intro frame).
const POSTER_TIME = 0.5;

export default function Testimonials() {
  const [playing, setPlaying] = useState(null); // index of the reel playing inline
  const videoRefs = useRef([]);
  const rowRef = useReveal({ stagger: 60, maxStaggered: 2 });

  // Once metadata is in, seek to POSTER_TIME so the element paints a real frame as its
  // thumbnail (no separate poster image / ffmpeg needed). Skipped for the playing clip.
  const showPoster = (i) => {
    const v = videoRefs.current[i];
    if (v && playing !== i && v.currentTime === 0) {
      try { v.currentTime = POSTER_TIME; } catch { /* ignore seek race */ }
    }
  };

  // Stop a clip and return it to its thumbnail: pause, mute, seek back to the poster frame.
  const stop = (v) => {
    if (!v) return;
    v.pause();
    v.muted = true;
    try { v.currentTime = POSTER_TIME; } catch { /* ignore seek race */ }
  };

  const play = (i) => {
    // Only one video plays at a time — stop every other card first.
    videoRefs.current.forEach((v, j) => { if (j !== i) stop(v); });
    setPlaying(i);
    const v = videoRefs.current[i];
    if (v) {
      v.muted = false;
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay may be blocked; controls remain */ });
    }
  };

  return (
    <section id="testimonials" className="reels">
      <div className="container">
        <header className="reels__header">
          <h2 className="reels__title">Trusted by leaders. Proven in the workplace.</h2>
          <p className="reels__sub">
            Leaders and participants on the impact of our programs, in their own words.
          </p>
        </header>

        <div className="reels__row" ref={rowRef}>
          {REELS.map((r, i) => (
            <figure className="reel" key={i}>
              <div className={`reel__frame ${playing === i ? 'is-playing' : ''}`}>
                <video
                  ref={(el) => { videoRefs.current[i] = el; }}
                  className="reel__video"
                  src={r.src}
                  muted
                  playsInline
                  preload="metadata"
                  controls={playing === i}
                  onLoadedMetadata={() => showPoster(i)}
                  onEnded={() => { setPlaying(null); stop(videoRefs.current[i]); }}
                  onClick={() => { if (playing !== i) play(i); }}
                />
                {playing !== i && (
                  <button
                    type="button"
                    className="reel__play-btn"
                    aria-label={`Play video: ${r.name}`}
                    onClick={() => play(i)}
                  >
                    <span className="reel__play" aria-hidden="true">
                      <Icon name="play" size={22} className="reel__play-icon" />
                    </span>
                  </button>
                )}
              </div>
              <figcaption className="reel__caption">
                <div className="reel__name">{r.name}</div>
                <div className="reel__meta">{[r.company, r.title].filter(Boolean).join(' · ')}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
